import {
  AlertCircle,
  CalendarDays,
  Download,
  Info,
  RefreshCw,
  SlidersHorizontal
} from "lucide-react";
import { useEffect, useEffectEvent, useMemo, useRef, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";

import {
  api,
  ApiError,
  REFERENCE_SINK_RATE_MS,
  staticWindgramUrl
} from "../api";
import { HourInspector } from "../components/HourInspector";
import { SiteSelector } from "../components/SiteSelector";
import { WindgramChart } from "../components/WindgramChart";
import "../forecast/forecastControls.css";
import {
  ForecastRequestGate,
  startForecastRefreshSchedule,
  type ForecastRefreshSchedule
} from "../forecast/forecastRefresh";
import { useLocale } from "../i18n/LocaleContext";
import {
  resolveInitialSiteState,
  serializeCanonicalSiteHref,
  type CanonicalSiteState,
  type ResolvedSiteState,
  writeSavedSiteSlug,
  writeSavedUnitPreference
} from "../site/siteState";
import { telemetrySiteSlugForSite } from "../site/siteTelemetry";
import {
  emitTelemetryEvent,
  telemetryFailureKindFromApiReason,
  telemetryFlightMode,
  telemetrySignatureForEvent
} from "../telemetry/product";
import type {
  FlightMode,
  ForecastQuery,
  ForecastModelsResponse,
  ForecastModelSelection,
  ForecastResponse,
  HeightFrame,
  SiteOption,
  UnitProfile
} from "../types";
import {
  convertHeightFromFeet,
  convertSpeedFromKnots,
  formatInputAirspeed,
  formatInputCruiseAltitude,
  parseInputAirspeed,
  parseInputCruiseAltitude,
  speedUnitLabel
} from "../units/conversions";
import {
  currentForecastAgeSeconds,
  formatAgeLabel,
  formatCycleLabel,
  formatForecastDay,
  formatSiteLocalTime
} from "../units/time";

const FORECAST_STALE_AFTER_SECONDS = 3 * 60 * 60;

const DEFAULT_PPG_INPUTS = {
  cruiseAirspeedKt: 25,
  courseDegrees: 180,
  cruiseAltitudeModelAglFt: 500,
  launchHeadingDegrees: 180
} satisfies Pick<
  ForecastQuery,
  | "cruiseAirspeedKt"
  | "courseDegrees"
  | "cruiseAltitudeModelAglFt"
  | "launchHeadingDegrees"
>;
type PpgInputs = Pick<
  ForecastQuery,
  | "cruiseAirspeedKt"
  | "courseDegrees"
  | "cruiseAltitudeModelAglFt"
  | "launchHeadingDegrees"
>;

export function modelSelectionForDay(
  requested: ForecastModelSelection,
  dayOffset: number,
  choices: ForecastModelsResponse | null
): ForecastModelSelection {
  if (requested === "auto") return requested;
  const choice = choices?.models.find((model) => model.id === requested);
  return choice && choice.availability.reason !== "outside_model_domain" && dayOffset <= choice.forecast_day_offsets
    ? requested
    : "auto";
}

type ForecastIssue = {
  title: string;
  detail: string;
};

function LoadingWindgram({ label }: { label: string }) {
  return (
    <div className="loading-panel" aria-live="polite">
      <div className="loading-sky" />
      <div className="loading-copy">
        <RefreshCw className="spin" size={20} />
        {label}
      </div>
    </div>
  );
}

function describeError(
  error: unknown,
  scope: "catalog" | "forecast",
  t: (key: string, options?: Record<string, unknown>) => string
): ForecastIssue {
  if (error instanceof ApiError) {
    if (error.kind === "invalid_payload") {
      return {
        title:
          scope === "catalog"
            ? t("errors.catalogUnreadableTitle")
            : t("errors.forecastUnreadableTitle"),
        detail:
          scope === "catalog"
            ? t("errors.catalogUnreadableDetail")
            : t("errors.forecastUnreadableDetail")
      };
    }
    if (error.kind === "network") {
      return {
        title:
          scope === "catalog" ? t("errors.catalogUnreachableTitle") : t("errors.forecastUnreachableTitle"),
        detail:
          scope === "catalog"
            ? t("errors.catalogUnreachableDetail")
            : t("errors.forecastUnreachableDetail")
      };
    }
    if (error.code) {
      const key = `models.reason.${error.code}`;
      const reason = t(key);
      if (reason !== key) {
        return {
          title: t("errors.forecastUnavailable"),
          detail: t("models.requestedUnavailable", { reason })
        };
      }
    }
    return {
      title:
        error.status === 503
          ? t("errors.upstreamUnavailable")
          : scope === "catalog"
            ? t("errors.catalogUnavailable")
            : t("errors.forecastUnavailable"),
      detail: error.message
    };
  }

  return {
    title: scope === "catalog" ? t("errors.catalogUnavailable") : t("errors.forecastUnavailable"),
    detail: error instanceof Error ? error.message : t("errors.unexpected")
  };
}

function serializeChooserHref(
  dayOffset: number,
  mode: FlightMode,
  selectedHour: number,
  units: UnitProfile,
  model: ForecastModelSelection
): string {
  const params = new URLSearchParams({
    day: String(dayOffset),
    mode: mode === "ppg" ? "ppg" : "pg",
    hour: String(selectedHour),
    units,
    model
  });
  return `/?${params.toString()}`;
}

export function resolveSelectedSiteState(
  site: SiteOption,
  search: string,
  sites: readonly SiteOption[],
  storage?: Storage | null
): CanonicalSiteState {
  const resolved = resolveInitialSiteState({
    pathname: site.canonical_path,
    search,
    sites,
    storage
  });
  if (resolved.kind === "site") {
    return { ...resolved, model: "auto" };
  }
  return {
    kind: "site",
    site,
    dayOffset: resolved.dayOffset,
    mode: resolved.mode,
    selectedHour: resolved.selectedHour,
    units: site.country_code.toLowerCase() === "us" ? "us" : "metric",
    model: "auto"
  };
}

function forecastTelemetryContext(
  resolvedState: ResolvedSiteState | null,
  overrides?: Partial<{
    site: SiteOption;
    dayOffset: number;
    mode: FlightMode;
    units: UnitProfile;
    selectedHour: number;
  }>
) {
  const site = overrides?.site ?? (resolvedState?.kind === "site" ? resolvedState.site : null);
  const siteSlug = telemetrySiteSlugForSite(site);
  if (siteSlug === null) {
    return null;
  }

  const dayOffset = overrides?.dayOffset ?? resolvedState?.dayOffset ?? 0;
  const mode = overrides?.mode ?? resolvedState?.mode ?? "free_flight";
  const units = overrides?.units ?? resolvedState?.units ?? "us";
  const selectedHour = overrides?.selectedHour;

  return {
    site_slug: siteSlug,
    day_offset: dayOffset,
    flight_mode: telemetryFlightMode(mode),
    unit_profile: units,
    ...(selectedHour === undefined ? {} : { selected_hour: selectedHour })
  };
}

export function WindgramPage() {
  const location = useLocation();
  const navigate = useNavigate();
  const { t } = useTranslation();
  const { barePathname, href: localizedHref, locale } = useLocale();
  const lastForecastTelemetrySignatureRef = useRef<string | null>(null);
  const forecastRequestGateRef = useRef(new ForecastRequestGate());
  const modelsRequestGateRef = useRef(new ForecastRequestGate());
  const forecastRequestInFlightRef = useRef(false);
  const lastForecastQueryRef = useRef<ForecastQuery | null>(null);
  const lastForecastRequestStartedAtRef = useRef(0);
  const forecastRefreshScheduleRef = useRef<ForecastRefreshSchedule | null>(null);

  const [sites, setSites] = useState<SiteOption[]>([]);
  const [catalogLoading, setCatalogLoading] = useState(true);
  const [catalogError, setCatalogError] = useState<unknown>(null);
  const [catalogReloadKey, setCatalogReloadKey] = useState(0);

  const [frame, setFrame] = useState<HeightFrame>("altitude_msl_ft");
  const [forecast, setForecast] = useState<ForecastResponse | null>(null);
  const [forecastLoading, setForecastLoading] = useState(false);
  const [forecastError, setForecastError] = useState<unknown>(null);
  const [forecastReloadKey, setForecastReloadKey] = useState(0);
  const [models, setModels] = useState<ForecastModelsResponse | null>(null);
  const [modelsLoading, setModelsLoading] = useState(false);
  const [modelsError, setModelsError] = useState(false);
  const [forecastClockMs, setForecastClockMs] = useState(() => Date.now());
  const [ppgInputs, setPpgInputs] = useState<PpgInputs>(DEFAULT_PPG_INPUTS);

  const postProductTelemetry = useEffectEvent(
    (
      event: Parameters<typeof emitTelemetryEvent>[0],
      context: Parameters<typeof emitTelemetryEvent>[1]
    ) => {
      void emitTelemetryEvent(event, context);
    }
  );
  const requestForecastRefresh = useEffectEvent(() => {
    setForecastReloadKey((value) => value + 1);
  });
  useEffect(() => {
    const controller = new AbortController();
    setCatalogLoading(true);
    setCatalogError(null);
    api
      .sites(controller.signal)
      .then((nextSites) => {
        setSites(nextSites);
        setCatalogLoading(false);
      })
      .catch((reason: unknown) => {
        if (reason instanceof ApiError && reason.kind === "aborted") {
          return;
        }
        setCatalogError(reason);
        setCatalogLoading(false);
        const failureKind = telemetryFailureKindFromApiReason(reason);
        if (failureKind !== null) {
          void emitTelemetryEvent("api_failure", {
            surface: "forecast_page",
            failure_kind: failureKind
          });
        }
      });
    return () => controller.abort();
  }, [catalogReloadKey]);

  const resolvedState = useMemo(() => {
    if (catalogLoading || sites.length === 0) {
      return null;
    }
    return resolveInitialSiteState({
      pathname: barePathname,
      search: location.search,
      sites
    });
  }, [barePathname, catalogLoading, location.search, sites]);

  const selectedSite = resolvedState?.kind === "site" ? resolvedState.site : null;
  const selectedHour = resolvedState?.selectedHour ?? 13;
  const units = resolvedState?.units ?? "us";
  const mode = resolvedState?.mode ?? "free_flight";
  const selectedDayOffset = resolvedState?.dayOffset ?? 0;
  const selectedModel = resolvedState?.model ?? "auto";
  const formatDecimal = useMemo(
    () => new Intl.NumberFormat(locale, { maximumFractionDigits: 1 }),
    [locale]
  );
  const reasonLabel = (reason: string | null | undefined) => {
    const key = `models.reason.${reason ?? "unknown"}`;
    const value = t(key);
    return value === key ? t("models.reason.unknown") : value;
  };

  useEffect(() => {
    if (resolvedState?.kind !== "site") {
      return;
    }
    writeSavedSiteSlug(resolvedState.site.slug);
    const nextHref = localizedHref(serializeCanonicalSiteHref(resolvedState));
    if (`${location.pathname}${location.search}` !== nextHref) {
      navigate(nextHref, { replace: true });
    }
  }, [localizedHref, location.pathname, location.search, navigate, resolvedState]);

  useEffect(() => {
    if (!selectedSite) {
      modelsRequestGateRef.current.begin();
      setModels(null);
      setModelsLoading(false);
      setModelsError(false);
      return;
    }
    const controller = new AbortController();
    const requestId = modelsRequestGateRef.current.begin();
    setModels(null);
    setModelsLoading(true);
    setModelsError(false);
    api.models(selectedSite.slug, selectedDayOffset, controller.signal)
      .then((value) => {
        if (!modelsRequestGateRef.current.isCurrent(requestId)) return;
        setModels(value);
        setModelsLoading(false);
      })
      .catch((reason: unknown) => {
        if (!modelsRequestGateRef.current.isCurrent(requestId)) return;
        if (!(reason instanceof ApiError && reason.kind === "aborted")) {
          setModelsLoading(false);
          setModelsError(true);
        }
      });
    return () => {
      controller.abort();
      modelsRequestGateRef.current.invalidate(requestId);
    };
  }, [selectedDayOffset, selectedSite]);

  const forecastQuery = useMemo<ForecastQuery | null>(() => {
    if (selectedSite === null) {
      return null;
    }
    return {
      site: selectedSite.slug,
      dayOffset: selectedDayOffset,
      mode,
      units,
      model: selectedModel,
      ...(mode === "ppg" ? ppgInputs : {})
    };
  }, [mode, ppgInputs, selectedDayOffset, selectedModel, selectedSite, units]);

  useEffect(() => {
    if (forecastQuery === null) {
      forecastRequestGateRef.current.begin();
      forecastRequestInFlightRef.current = false;
      lastForecastQueryRef.current = null;
      lastForecastTelemetrySignatureRef.current = null;
      setForecast(null);
      setForecastLoading(false);
      setForecastError(null);
      return;
    }
    const queryChanged = lastForecastQueryRef.current !== forecastQuery;
    lastForecastQueryRef.current = forecastQuery;
    const controller = new AbortController();
    const requestId = forecastRequestGateRef.current.begin();
    forecastRequestInFlightRef.current = true;
    lastForecastRequestStartedAtRef.current = Date.now();
    if (queryChanged) {
      setForecast(null);
    }
    setForecastLoading(true);
    setForecastError(null);
    api
      .forecast(forecastQuery, controller.signal)
      .then((nextForecast) => {
        if (!forecastRequestGateRef.current.isCurrent(requestId)) {
          return;
        }
        setForecast(nextForecast);
        setForecastLoading(false);
        forecastRequestInFlightRef.current = false;
        setForecastClockMs(Date.now());
        const context = forecastTelemetryContext(null, {
          site: selectedSite ?? undefined,
          dayOffset: selectedDayOffset,
          mode,
          units
        });
        if (context !== null) {
          const signature = telemetrySignatureForEvent("forecast_loaded", context);
          if (
            signature !== null &&
            signature !== lastForecastTelemetrySignatureRef.current
          ) {
            lastForecastTelemetrySignatureRef.current = signature;
            postProductTelemetry("forecast_loaded", context);
          }
        }
      })
      .catch((reason: unknown) => {
        if (!forecastRequestGateRef.current.isCurrent(requestId)) {
          return;
        }
        if (reason instanceof ApiError && reason.kind === "aborted") {
          forecastRequestInFlightRef.current = false;
          return;
        }
        setForecastError(reason);
        setForecastLoading(false);
        forecastRequestInFlightRef.current = false;
        const failureKind = telemetryFailureKindFromApiReason(reason);
        const context = forecastTelemetryContext(null, {
          site: selectedSite ?? undefined,
          dayOffset: selectedDayOffset,
          mode,
          units
        });
        if (failureKind !== null) {
          void emitTelemetryEvent("api_failure", {
            surface: "forecast_page",
            failure_kind: failureKind,
            ...(context ?? {})
          });
        }
      });
    return () => {
      controller.abort();
      forecastRequestGateRef.current.invalidate(requestId);
    };
  }, [
    forecastQuery,
    forecastReloadKey,
    mode,
    selectedDayOffset,
    selectedSite,
    units
  ]);

  useEffect(() => {
    if (forecastQuery === null || selectedSite === null) {
      forecastRefreshScheduleRef.current = null;
      return;
    }

    const schedule = startForecastRefreshSchedule({
      environment: {
        now: () => Date.now(),
        isVisible: () => document.visibilityState === "visible",
        setInterval: (callback, delayMs) => window.setInterval(callback, delayMs),
        clearInterval: (timerId) => window.clearInterval(timerId),
        subscribeVisibility: (callback) => {
          document.addEventListener("visibilitychange", callback);
          return () => document.removeEventListener("visibilitychange", callback);
        },
        subscribeFocus: (callback) => {
          window.addEventListener("focus", callback);
          return () => window.removeEventListener("focus", callback);
        }
      },
      timezone: selectedSite.timezone,
      initialRefreshAtMs: lastForecastRequestStartedAtRef.current || Date.now(),
      isRefreshInFlight: () => forecastRequestInFlightRef.current,
      onClock: setForecastClockMs,
      onRefresh: requestForecastRefresh
    });
    forecastRefreshScheduleRef.current = schedule;

    return () => {
      schedule.stop();
      if (forecastRefreshScheduleRef.current === schedule) {
        forecastRefreshScheduleRef.current = null;
      }
    };
  }, [forecastQuery, selectedSite]);

  useEffect(() => {
    if (forecast === null || resolvedState?.kind !== "site" || forecast.hours.length === 0) {
      return;
    }
    if (forecast.hours.some((hour) => hour.hour === resolvedState.selectedHour)) {
      return;
    }
    navigate(
      localizedHref(serializeCanonicalSiteHref({
        ...resolvedState,
        selectedHour: forecast.hours[0].hour
      })),
      { replace: true }
    );
  }, [forecast, navigate, resolvedState]);

  const selected = useMemo(
    () => forecast?.hours.find((hour) => hour.hour === selectedHour) ?? forecast?.hours[0],
    [forecast, selectedHour]
  );

  const selectSite = (site: SiteOption | null) => {
    if (site === null || resolvedState === null) {
      return;
    }
    const nextState = resolveSelectedSiteState(site, location.search, sites);
    if (selectedSite?.slug !== nextState.site.slug) {
      const context = forecastTelemetryContext(resolvedState, {
        site: nextState.site,
        dayOffset: nextState.dayOffset,
        mode: nextState.mode,
        units: nextState.units
      });
      if (context) {
        postProductTelemetry("site_selected", context);
      }
    }
    writeSavedSiteSlug(site.slug);
    navigate(localizedHref(serializeCanonicalSiteHref(nextState)));
  };

  const updateRouteState = (
    patch: Partial<{
      site: SiteOption;
      dayOffset: number;
      mode: FlightMode;
      selectedHour: number;
      units: UnitProfile;
      model: ForecastModelSelection;
    }>
  ) => {
    if (resolvedState === null) {
      return;
    }
    if (resolvedState.kind === "site") {
      const nextState = {
        kind: "site" as const,
        site: patch.site ?? resolvedState.site,
        dayOffset: patch.dayOffset ?? resolvedState.dayOffset,
        mode: patch.mode ?? resolvedState.mode,
        selectedHour: patch.selectedHour ?? resolvedState.selectedHour,
        units: patch.units ?? resolvedState.units,
        model: patch.model ?? resolvedState.model
      };
      writeSavedSiteSlug(nextState.site.slug);
      if (patch.units !== undefined) {
        writeSavedUnitPreference(nextState.units);
      }
      navigate(localizedHref(serializeCanonicalSiteHref(nextState)));
      return;
    }

    const nextUnits = patch.units ?? resolvedState.units;
    if (patch.units !== undefined) {
      writeSavedUnitPreference(nextUnits);
    }
    navigate(
      localizedHref(serializeChooserHref(
        patch.dayOffset ?? resolvedState.dayOffset,
        patch.mode ?? resolvedState.mode,
        patch.selectedHour ?? resolvedState.selectedHour,
        nextUnits,
        patch.model ?? resolvedState.model
      ))
    );
  };

  const setMode = (nextMode: FlightMode) => {
    if (nextMode === mode) {
      return;
    }
    if (nextMode === "ppg") {
      setPpgInputs((current) => ({
        cruiseAirspeedKt: current.cruiseAirspeedKt ?? DEFAULT_PPG_INPUTS.cruiseAirspeedKt,
        courseDegrees: current.courseDegrees ?? DEFAULT_PPG_INPUTS.courseDegrees,
        cruiseAltitudeModelAglFt:
          current.cruiseAltitudeModelAglFt ??
          DEFAULT_PPG_INPUTS.cruiseAltitudeModelAglFt,
        launchHeadingDegrees:
          current.launchHeadingDegrees ?? DEFAULT_PPG_INPUTS.launchHeadingDegrees
      }));
    }
    const context = forecastTelemetryContext(resolvedState, { mode: nextMode });
    if (context) {
      postProductTelemetry("flight_mode_changed", context);
    }
    updateRouteState({ mode: nextMode });
  };

  const updateHeading = (
    key: "courseDegrees" | "launchHeadingDegrees",
    value: string
  ) => {
    setPpgInputs((current) => ({
      ...current,
      [key]: value.trim() === "" ? undefined : Number(value)
    }));
  };

  const updateCruiseAirspeed = (value: string) => {
    setPpgInputs((current) => ({
      ...current,
      cruiseAirspeedKt: parseInputAirspeed(value, units)
    }));
  };

  const updateCruiseAltitude = (value: string) => {
    setPpgInputs((current) => ({
      ...current,
      cruiseAltitudeModelAglFt: parseInputCruiseAltitude(value, units)
    }));
  };

  const status = forecast?.freshness ?? null;
  const liveFetchAgeSeconds = status
    ? currentForecastAgeSeconds(status, forecastClockMs)
    : null;
  const forecastIsStale =
    liveFetchAgeSeconds === null
      ? status?.is_stale === true
      : liveFetchAgeSeconds > FORECAST_STALE_AFTER_SECONDS;
  const forecastIssue = forecastError ? describeError(forecastError, "forecast", t) : null;
  const catalogIssue = catalogError ? describeError(catalogError, "catalog", t) : null;
  const staticHref = forecastQuery ? staticWindgramUrl(forecastQuery, locale) : null;
  const providerLabel = (provider: string) => {
    const key = `models.provider.${provider}`;
    const translated = t(key);
    return translated === key
      ? provider.replaceAll("_", " ").replace(/\b\w/g, (letter) => letter.toUpperCase())
      : translated;
  };

  return (
    <div className="page windgram-page">
      <section className="control-deck" aria-label={t("controls.controlsA11y")}>
        <div>
          <span className="eyebrow">{t("controls.launch")}</span>
          <SiteSelector
            defaultOpen={false}
            disabled={catalogLoading || catalogError !== null}
            onChange={selectSite}
            label={t("controls.launch")}
            placeholder={t("controls.chooseLaunch")}
            sites={sites}
            value={selectedSite?.slug ?? null}
          />
          <div className="control-note">
            {selectedSite
              ? selectedSite.full_location
              : t("controls.chooseLaunch")}
          </div>
        </div>

        <div className="control-group">
          <span>
            <CalendarDays size={14} /> {t("controls.forecastDay")}
          </span>
          <div className="segmented">
            {[0, 1, 2].map((offset) => (
              <button
                className={resolvedState?.dayOffset === offset ? "active" : ""}
                key={offset}
                onClick={() => {
                  if (resolvedState?.dayOffset !== offset) {
                    const context = forecastTelemetryContext(resolvedState, {
                      dayOffset: offset
                    });
                    if (context) {
                      postProductTelemetry("day_changed", context);
                    }
                  }
                  updateRouteState({
                    dayOffset: offset,
                    model: modelSelectionForDay(selectedModel, offset, models)
                  });
                }}
                type="button"
              >
                {offset === 0 ? t("controls.today") : offset === 1 ? t("controls.tomorrow") : t("controls.inDays", { count: offset })}
              </button>
            ))}
          </div>
        </div>

        <div className="control-group mode-control">
          <span>{t("controls.flightMode")}</span>
          <div className="segmented">
            <button
              className={mode === "free_flight" ? "active" : ""}
              onClick={() => setMode("free_flight")}
              type="button"
            >
              {t("controls.freeFlight")}
            </button>
            <button
              className={mode === "ppg" ? "active" : ""}
              onClick={() => setMode("ppg")}
              type="button"
            >
              {t("controls.paramotor")}
            </button>
          </div>
        </div>

        <div className="control-group units-control">
          <span>{t("controls.units")}</span>
          <div className="segmented">
            <button
              className={units === "us" ? "active" : ""}
              onClick={() => {
                if (units !== "us") {
                  const context = forecastTelemetryContext(resolvedState, {
                    units: "us"
                  });
                  if (context) {
                    postProductTelemetry("unit_profile_changed", context);
                  }
                }
                updateRouteState({ units: "us" });
              }}
              type="button"
            >
              {t("controls.usUnits")}
            </button>
            <button
              className={units === "metric" ? "active" : ""}
              onClick={() => {
                if (units !== "metric") {
                  const context = forecastTelemetryContext(resolvedState, {
                    units: "metric"
                  });
                  if (context) {
                    postProductTelemetry("unit_profile_changed", context);
                  }
                }
                updateRouteState({ units: "metric" });
              }}
              type="button"
            >
              {t("controls.metricUnits")}
            </button>
          </div>
        </div>

        <label className="control-group model-control">
          <span>{t("models.label")}</span>
          <select
            value={selectedModel}
            disabled={!selectedSite || modelsLoading}
            onChange={(event) => updateRouteState({ model: event.target.value })}
          >
            <option value="auto">{t("models.auto")}</option>
            {models?.models.map((model) => (
              <option key={model.id} value={model.id} disabled={!model.availability.eligible}>
                {model.label} · {formatDecimal.format(model.resolution_km)} km{model.availability.eligible ? "" : ` — ${reasonLabel(model.availability.reason)}`}
              </option>
            ))}
          </select>
          <small>{modelsLoading ? t("models.loading") : modelsError ? t("models.choicesUnavailable") : t("models.autoDescription")}</small>
        </label>
      </section>

      {mode === "ppg" && (
        <section className="ppg-controls">
          <div className="ppg-control-heading">
            <SlidersHorizontal size={16} />
            <span>{t("inspector.missionGeometry")}</span>
          </div>
          <label>
            {t("controls.airspeed")}
            <input
              type="number"
              min="1"
              max={Math.round(convertSpeedFromKnots(100, units))}
              value={formatInputAirspeed(ppgInputs.cruiseAirspeedKt, units)}
              onChange={(event) => updateCruiseAirspeed(event.target.value)}
            />
            <span>{speedUnitLabel(units)}</span>
          </label>
          <label>
            {t("controls.course")}
            <input
              type="number"
              min="0"
              max="359"
              value={ppgInputs.courseDegrees ?? ""}
              onChange={(event) => updateHeading("courseDegrees", event.target.value)}
            />
            <span>°T</span>
          </label>
          <label>
            {t("controls.altitude")}
            <input
              type="number"
              min="0"
              max={Math.round(convertHeightFromFeet(20000, units))}
              step={units === "metric" ? "50" : "100"}
              value={formatInputCruiseAltitude(ppgInputs.cruiseAltitudeModelAglFt, units)}
              onChange={(event) => updateCruiseAltitude(event.target.value)}
            />
            <span>{t("svg.modelAgl", { unit: units === "metric" ? "m" : "ft" })}</span>
          </label>
          <label>
            {t("controls.launchHeading")}
            <input
              type="number"
              min="0"
              max="359"
              value={ppgInputs.launchHeadingDegrees ?? ""}
              onChange={(event) =>
                updateHeading("launchHeadingDegrees", event.target.value)
              }
            />
            <span>°T</span>
          </label>
        </section>
      )}

      <section className="windgram-toolbar">
        <div className="frame-control">
          <span>{t("controls.heightFrame")}</span>
          <div className="segmented compact">
            {(
              [
                ["altitude_msl_ft", t("controls.msl")],
                ["altitude_model_agl_ft", t("controls.modelAgl")],
                ["altitude_launch_agl_ft", t("controls.launchAgl")]
              ] as const
            ).map(([value, label]) => (
              <button
                type="button"
                key={value}
                className={frame === value ? "active" : ""}
                onClick={() => setFrame(value)}
              >
                {label}
              </button>
            ))}
          </div>
        </div>
        <div className="legend-inline" aria-label={t("charts.legendA11y")}>
          <span><i className="legend-line thermal" /> {t("charts.thermalTop")}</span>
          <span><i className="legend-line sensitivity" /> {t("charts.sensitivityLegend")}</span>
          <span><i className="legend-canopy">⌁</i> {t("charts.usableTop")}</span>
          <span><i className="legend-cloud">●</i> {t("charts.cloudbase")}</span>
        </div>
        {staticHref ? (
          <a
            className="button secondary"
            href={staticHref}
            download
            onClick={() => {
              const context = forecastTelemetryContext(resolvedState);
              if (context) {
                postProductTelemetry("static_svg_opened", context);
              }
            }}
          >
            <Download size={15} /> {t("controls.downloadSvg")}
          </a>
        ) : null}
        {forecast ? (
          <button
            aria-label={t("controls.refresh")}
            className="button secondary forecast-refresh-button"
            disabled={forecastLoading}
            onClick={() => forecastRefreshScheduleRef.current?.refreshNow()}
            type="button"
          >
            <RefreshCw className={forecastLoading ? "spin" : undefined} size={15} />
            {forecastLoading ? t("common.loading") : t("controls.refresh")}
          </button>
        ) : null}
      </section>

      <section className="reference-sink-note" aria-label={t("charts.referenceSinkA11y")}>
        {t("charts.referenceSink", {
          sink: new Intl.NumberFormat(locale, {
            minimumFractionDigits: 2,
            maximumFractionDigits: 2,
            useGrouping: false
          }).format(REFERENCE_SINK_RATE_MS)
        })}
      </section>

      {catalogIssue ? (
        <section className="error-panel">
          <AlertCircle size={24} />
          <div>
            <strong>{catalogIssue.title}</strong>
            <p>{catalogIssue.detail}</p>
          </div>
          <button type="button" onClick={() => setCatalogReloadKey((value) => value + 1)}>
            {t("common.retry")}
          </button>
        </section>
      ) : catalogLoading && resolvedState === null ? (
        <LoadingWindgram label={t("common.loading")} />
      ) : resolvedState?.kind === "chooser" ? (
        <section className="chooser-panel">
          <h2>
            {resolvedState.reason === "invalid_url_site"
              ? t("controls.invalidLaunchUrl")
              : t("controls.chooseLaunchBegin")}
          </h2>
          <p className="chooser-copy">
            {t("controls.urlStateHint")}
          </p>
          <SiteSelector
            defaultOpen
            defaultQuery=""
            onChange={selectSite}
            label={t("controls.launch")}
            placeholder={t("controls.searchLaunches")}
            sites={sites}
            value={null}
          />
        </section>
      ) : forecastIssue && forecast === null ? (
        <section className="error-panel">
          <AlertCircle size={24} />
          <div>
            <strong>{forecastIssue.title}</strong>
            <p>{forecastIssue.detail}</p>
          </div>
          <button
            type="button"
            onClick={() => forecastRefreshScheduleRef.current?.refreshNow()}
          >
            {t("common.retry")}
          </button>
        </section>
      ) : forecast === null || selected === undefined ? (
        <LoadingWindgram label={t("charts.building")} />
      ) : (
        <>
          <section className="model-provenance" aria-label={t("a11y.modelStatus")} aria-live="polite">
            <strong>{forecast.model_metadata ? t("models.actual", {
              model: forecast.model_metadata.label,
              resolution: formatDecimal.format(forecast.model_metadata.resolution_km)
            }) : forecast.model}</strong>
            {forecast.model_metadata?.provider ? <span>{t("models.providedBy", { provider: providerLabel(forecast.model_metadata.provider) })}</span> : null}
            {forecast.model_selection?.reason ? <span>{t(
              forecast.model_selection.reason === "fallback_after_upstream_unavailable" ? "models.fallback" : "models.selection",
              { reason: reasonLabel(forecast.model_selection.reason) }
            )}</span> : null}
          </section>
          {status ? (
            <section
              className={`windgram-status${forecastIsStale ? " stale" : ""}`}
              aria-live="polite"
            >
              <strong>
                {forecastIsStale
                  ? t("models.freshnessStale")
                  : t("models.freshnessCurrent")}
              </strong>
              <div className="windgram-status-lines">
                <span>
                  {t("models.fetched", { time: formatSiteLocalTime(status.fetched_at, status.site_timezone, locale, t("common.unknown")) })}
                </span>
                <span>
                  {t("models.checked", { time: formatSiteLocalTime(status.checked_at, status.site_timezone, locale, t("common.unknown")) })}
                </span>
                <span>
                  {t("models.age", { age: liveFetchAgeSeconds === null ? t("common.unknown") : formatAgeLabel(liveFetchAgeSeconds, locale) })}
                </span>
                {forecast.model.toLowerCase().includes("hrrr") ? <span>
                  {t("models.providerCycle", { cycle: status.latest_noaa_cycle ? formatCycleLabel(status.latest_noaa_cycle, status.site_timezone, locale) : t("common.unknown") })}
                </span> : null}
                <span>{t("models.servedCycle", { cycle: t("common.unknown") })}</span>
                {forecastIssue ? (
                  <span className="forecast-refresh-warning">
                    {t("models.refreshFailed")}
                  </span>
                ) : null}
              </div>
            </section>
          ) : null}

          <div className="windgram-workspace">
            <div>
              <WindgramChart
                forecast={forecast}
                frame={frame}
                units={units}
                selectedHour={selected.hour}
                onSelectHour={(hour) => {
                  if (selected.hour !== hour) {
                    const context = forecastTelemetryContext(resolvedState, {
                      selectedHour: hour
                    });
                    if (context) {
                      postProductTelemetry("hour_inspected", context);
                    }
                  }
                  updateRouteState({ selectedHour: hour });
                }}
              />
              <div className="chart-caption">
                <div>
                  <Info size={15} />
                  {t("charts.caption")}
                </div>
                <span>
                  {formatForecastDay(forecast.day, locale)} · {selectedSite?.timezone ?? t("charts.siteTime")}
                </span>
              </div>
            </div>
            <HourInspector hour={selected} forecast={forecast} frame={frame} units={units} />
          </div>
        </>
      )}
    </div>
  );
}
